import React, { useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormHelperText,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { useGetSalesIds } from '../../api/sales'
import { useGetCompanyMappingOptions } from '../../api/company-mapping'
import { useGetHTGIncentiveTypes } from '../../api/point'

const MIN_EXTENDED_DATE = '2026-07-01'

const numberField = yup
  .number()
  .transform((value, originalValue) =>
    originalValue === '' || originalValue === null ? undefined : value
  )
  .typeError('Must be a number')
  .min(0, 'Must be positive')
  .nullable()
  .notRequired()

const phoneField = yup
  .string()
  .nullable()
  .transform((value, originalValue) => (originalValue === '' ? null : value))
  .matches(/^[0-9]+$/, {
    message: 'Phone number must contain only numbers',
    excludeEmptyString: true,
  })
  .min(8, 'Phone number must be at least 8 digits')
  .notRequired()

const validationSchema = yup.object().shape({
  id: yup.string().required('Company is required'),
  phoneNum: phoneField,
  hwtTarget: numberField,
  hteTarget: numberField,
  ektTarget: numberField,
  htbTarget: numberField,
  htlTarget: numberField,
  primarySalesId: yup.string().required('Primary Sales ID is required'),
  type: yup.string().required('type is required'),
  bonusEnabled: yup.boolean(),
  boosterPoint: numberField,
  extendedDate: yup
    .string()
    .nullable()
    .test('min-date', `Extended date must be after ${MIN_EXTENDED_DATE}`, (value) => {
      if (!value) return true

      return value >= MIN_EXTENDED_DATE
    }),
})

const Form = ({ isEdit, defaultValues, setOpen, open, api, refetch }) => {
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = api()
  const { data: salesOptions = [], isLoading: isLoadingSalesOptions } = useGetSalesIds()
  const { data: cmOptions = [], isLoading: isLoadingCMOptions } = useGetCompanyMappingOptions()
  const { data: typeOptions = [], isLoading: isLoadingTypeOptions } = useGetHTGIncentiveTypes()

  const onClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      refetch()
      enqueueSnackbar(`Successfully ${isEdit ? 'updated' : 'created'}`, { variant: 'success' })
      onClose()
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  useEffect(() => reset({ ...defaultValues }), [reset, defaultValues])

  const extendedDateEnabled = watch('extendedDateEnabled')

  useEffect(() => {
    // Set extended date to null on unchecked
    if (!extendedDateEnabled) {
      setValue('extendedDate', null)
    }
  }, [extendedDateEnabled, setValue])

  return (
    <Dialog maxWidth='md' open={open} fullWidth>
      <DialogTitle>HTG Incentive</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    options={cmOptions}
                    getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
                    value={cmOptions.find((option) => option.id === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue ? newValue.id : '')}
                    loading={isLoadingCMOptions}
                    disabled={isEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Company'
                        variant='outlined'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='phoneNum'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Phone Num'
                    variant='outlined'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='type'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    options={typeOptions}
                    value={field.value || null}
                    onChange={(_, v) => field.onChange(v)}
                    loading={isLoadingTypeOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Type'
                        variant='outlined'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='primarySalesId'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    options={salesOptions}
                    value={field.value || null}
                    onChange={(_, v) => field.onChange(v)}
                    loading={isLoadingSalesOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Primary Sales ID'
                        variant='outlined'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name='hwtTarget'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='HWT Target'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name='hteTarget'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='HTE Target'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name='ektTarget'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='EKT Target'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='htbTarget'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='HTB Target'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='htlTarget'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='HTL Target'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='boosterPoint'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Booster Point'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='bonusEnabled'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={!!error}>
                    <FormControlLabel
                      label='Bonus Enabled'
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                        />
                      }
                    />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='extendedDateEnabled'
                control={control}
                defaultValue={false}
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={!!error}>
                    <FormControlLabel
                      label='Extended Date Enabled'
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                        />
                      }
                    />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='extendedDate'
                control={control}
                disabled={!extendedDateEnabled}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='date'
                    label='Extended Date'
                    variant='outlined'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: MIN_EXTENDED_DATE,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={onClose}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            SUBMIT
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default Form
